import React, { useEffect, useState, useCallback } from "react";
import { api, apiErr } from "../lib/api";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Sprout, Trash2, CheckCircle2, ChevronDown, ChevronRight, AlertTriangle, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { GuidedReplacement } from "./GuidedReplacement";

export function BaselineSeeding() {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(null);
  const [records, setRecords] = useState({});
  const [confirm, setConfirm] = useState(null);
  const [guided, setGuided] = useState(false);
  const load = useCallback(() => api.get("/baseline-seed/status").then((r) => {
    setD(r.data);
    window.dispatchEvent(new Event("baseline-changed"));
  }).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const doSeed = async (groups, includeWithActual = false) => {
    setBusy(true);
    try {
      const r = await api.post("/baseline-seed/seed", { groups, include_with_actual: includeWithActual });
      const seeded = Object.entries(r.data.seeded);
      const withActual = Object.entries(r.data.skipped).filter(([, why]) => why === "has actual data");
      if (seeded.length) toast.success(`Seeded ${seeded.reduce((a, [, n]) => a + n, 0)} synthetic records (${seeded.map(([g]) => g).join(", ")})`);
      if (withActual.length && !includeWithActual) {
        setConfirm({ type: "actual", groups: withActual.map(([g]) => g) });
      } else if (!seeded.length) toast.info("Nothing to seed — groups already contain synthetic data");
      load();
    } catch (e) { toast.error(apiErr(e)); }
    setBusy(false);
  };

  const doPurge = async (group) => {
    setBusy(true);
    try {
      const r = await api.post("/baseline-seed/purge", { group });
      toast.success(`Purged ${r.data.total} synthetic record(s) — actual data untouched`);
      setConfirm(null); setRecords({}); load();
    } catch (e) { toast.error(apiErr(e)); }
    setBusy(false);
  };

  const loadRecords = (g) => api.get(`/baseline-seed/records?group=${g}`).then((r) => setRecords((p) => ({ ...p, [g]: r.data.records }))).catch(() => {});

  const declare = async (g, rec) => {
    try {
      await api.post("/baseline-seed/declare", { collection: rec.collection, id: rec.id });
      toast.success(`"${rec.label}" declared as actual — badge removed, excluded from purge`);
      loadRecords(g); load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  if (!d) return <div className="text-sm text-muted-foreground font-mono">Loading baseline seeding…</div>;
  const canEdit = d.can_edit;

  return (
    <div className="space-y-4" data-testid="baseline-seeding-panel">
      <div className="card-flat p-4 flex items-start justify-between gap-4 flex-wrap">
        <div className="max-w-2xl">
          <div className="font-display font-semibold flex items-center gap-2">
            <Sprout className="h-4 w-4 text-accent" /> Baseline Seeding
            {d.total_synthetic > 0 && <Badge variant="warning" className="text-[9px]" data-testid="bs-total-badge">{d.total_synthetic} synthetic records live</Badge>}
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            Generate a realistic, industry-specific example dataset so you can see exactly what data your reporting,
            risks, targets and initiatives need — then replace it with real data at your pace. Every seeded record is
            permanently marked <Badge variant="warning" className="text-[9px] mx-0.5">Synthetic</Badge>, can be purged in
            one click, or <span className="text-foreground font-medium">edited and declared as Actual</span> once reviewed —
            a record keeps a permanent <Badge variant="outline" className="text-[9px] mx-0.5 border-[#FFB800]/40 text-[#FFB800]">Originally synthetic</Badge> provenance marker after promotion.
          </p>
          <p className="text-[11px] text-muted-foreground mt-1.5" data-testid="bs-profile">
            Industry profile: <span className="text-foreground font-medium">{d.profile?.label}</span>
            {d.profile?.fallback && <span className="text-[#FFB800]"> (no detailed profile for your industry — cross-industry baseline used)</span>}
          </p>
        </div>
        {canEdit && (
          <div className="flex gap-2 flex-wrap">
            {d.total_synthetic > 0 && (
              <Button variant="primary" size="sm" onClick={() => setGuided(true)} data-testid="bs-guided-btn">
                <Wand2 className="h-3.5 w-3.5" /> Start guided replacement
              </Button>
            )}
            <Button variant={d.total_synthetic > 0 ? "outline" : "primary"} size="sm" disabled={busy} onClick={() => doSeed(null)} data-testid="bs-seed-all-btn">
              <Sprout className="h-3.5 w-3.5" /> Seed my baseline
            </Button>
            <Button variant="outline" size="sm" disabled={busy || d.total_synthetic === 0} onClick={() => setConfirm({ type: "purge", group: null })} data-testid="bs-purge-all-btn">
              <Trash2 className="h-3.5 w-3.5" /> Purge all synthetic data
            </Button>
          </div>
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        {d.groups.map((g) => (
          <div key={g.key} className="card-flat p-3.5" data-testid={`bs-group-${g.key}`}>
            <div className="flex items-center justify-between gap-2">
              <span className="font-medium text-sm">{g.label}</span>
              <div className="flex gap-1.5">
                {g.synthetic > 0 && <Badge variant="warning" className="text-[9px]" data-testid={`bs-syn-count-${g.key}`}>{g.synthetic} synthetic</Badge>}
                <Badge variant="outline" className="text-[9px]">{g.actual} actual</Badge>
              </div>
            </div>
            <p className="text-[11px] text-muted-foreground mt-1">{g.desc}</p>
            {g.actual > 0 && g.synthetic === 0 && (
              <p className="text-[10px] text-[#FFB800] mt-1 flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> This group already has actual data — seeding will add clearly-badged examples alongside it.</p>
            )}
            <div className="flex items-center gap-1.5 mt-2">
              {canEdit && g.synthetic === 0 && (
                <Button size="sm" variant="outline" className="h-6 text-[10px]" disabled={busy}
                  onClick={() => (g.actual > 0 ? setConfirm({ type: "actual", groups: [g.key] }) : doSeed([g.key]))}
                  data-testid={`bs-seed-${g.key}`}>Seed</Button>
              )}
              {canEdit && g.synthetic > 0 && (
                <Button size="sm" variant="ghost" className="h-6 text-[10px] text-red-400" disabled={busy}
                  onClick={() => setConfirm({ type: "purge", group: g.key })} data-testid={`bs-purge-${g.key}`}>Purge</Button>
              )}
              {g.synthetic > 0 && (
                <Button size="sm" variant="ghost" className="h-6 text-[10px]"
                  onClick={() => { setOpen(open === g.key ? null : g.key); if (open !== g.key) loadRecords(g.key); }}
                  data-testid={`bs-toggle-${g.key}`}>
                  {open === g.key ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />} Review & declare
                </Button>
              )}
            </div>
            {open === g.key && (
              <div className="mt-2 space-y-1 max-h-56 overflow-y-auto border-t border-border pt-2">
                {(records[g.key] || []).map((rec) => (
                  <div key={`${rec.collection}-${rec.id}`} className="flex items-center justify-between gap-2 text-[11px]">
                    <span className="truncate"><Badge variant="warning" className="text-[8px] mr-1">Synthetic</Badge>{rec.label} <span className="text-muted-foreground">· {rec.sub}</span></span>
                    {canEdit && (
                      <div className="flex items-center gap-1 shrink-0">
                        <Button size="sm" variant="ghost" className="h-5 text-[9px]" onClick={() => setGuided(true)} data-testid={`bs-edit-${rec.id}`}>
                          <Wand2 className="h-2.5 w-2.5" /> Edit first
                        </Button>
                        <Button size="sm" variant="ghost" className="h-5 text-[9px]" onClick={() => declare(g.key, rec)} data-testid={`bs-declare-${rec.id}`}>
                          <CheckCircle2 className="h-2.5 w-2.5" /> Declare actual
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
                {(records[g.key] || []).length === 0 && <div className="text-[10px] text-muted-foreground">Loading…</div>}
              </div>
            )}
          </div>
        ))}
      </div>

      {guided && <GuidedReplacement onClose={() => { setGuided(false); load(); }} onChanged={() => { setRecords({}); load(); }} />}

      <Dialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <DialogContent data-testid="bs-confirm-dialog">
          <DialogHeader><DialogTitle className="font-display">
            {confirm?.type === "purge" ? "Purge synthetic data?" : "Group already has actual data"}
          </DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            {confirm?.type === "purge"
              ? `All synthetic records ${confirm.group ? `in "${d.groups.find((g) => g.key === confirm.group)?.label}"` : "across every group"} will be deleted. Actual data (including records you declared as actual) is never touched.`
              : `${(confirm?.groups || []).map((k) => d.groups.find((g) => g.key === k)?.label).join(", ")} already contain(s) actual data. Add clearly-badged synthetic examples alongside it, or skip?`}
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setConfirm(null)} data-testid="bs-confirm-cancel">{confirm?.type === "purge" ? "Cancel" : "Skip"}</Button>
            {confirm?.type === "purge"
              ? <Button variant="primary" size="sm" disabled={busy} onClick={() => doPurge(confirm.group)} data-testid="bs-confirm-purge">Purge</Button>
              : <Button variant="primary" size="sm" disabled={busy} onClick={() => { const gs = confirm.groups; setConfirm(null); doSeed(gs, true); }} data-testid="bs-confirm-seed-anyway">Add examples anyway</Button>}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function SyntheticBanner({ to = "/data-management?tab=baseline" }) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    const fetchCount = () => api.get("/baseline-seed/status").then((r) => setCount(r.data.total_synthetic)).catch(() => {});
    fetchCount();
    window.addEventListener("baseline-changed", fetchCount);
    return () => window.removeEventListener("baseline-changed", fetchCount);
  }, []);
  if (!count) return null;
  return (
    <div className="border border-[#FFB800]/50 bg-[#FFB800]/10 rounded-sm px-3 py-2 text-xs flex items-center gap-2 flex-wrap" data-testid="synthetic-banner">
      <AlertTriangle className="h-3.5 w-3.5 text-[#FFB800] shrink-0" />
      <span>Dashboards currently include <b>{count} synthetic example record(s)</b> from baseline seeding — replace them with real data or purge them any time.</span>
      <a href={to} className="underline text-[#FFB800] hover:opacity-80" data-testid="synthetic-banner-link">Open Baseline Seeding</a>
    </div>
  );
}
